import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { useGetBacklinksHubQuery, useToggleBacklinkPostedMutation, BacklinkHubItem } from '../store/topicApi';
import Pagination from './Pagination';

const ITEMS_PER_PAGE = 15;

type FilterStatus = 'all' | 'pending' | 'posted';

const BacklinksHub: React.FC = () => {
  const { data, isLoading, error } = useGetBacklinksHubQuery();
  const [togglePosted] = useToggleBacklinkPostedMutation();
  const [currentPage, setCurrentPage] = useState(1);
  const [filter, setFilter] = useState<FilterStatus>('all');
  const [search, setSearch] = useState('');

  const backlinks = data?.backlinks || [];
  const filtered = backlinks.filter(b => {
    if (filter === 'posted' && !b.posted) return false;
    if (filter === 'pending' && b.posted) return false;
    if (search) {
      const q = search.toLowerCase();
      return b.title.toLowerCase().includes(q) || b.blogTitle.toLowerCase().includes(q) || b.url.toLowerCase().includes(q);
    }
    return true;
  });

  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const paginated = filtered.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  const copyText = (text: string, label: string) => {
    navigator.clipboard.writeText(text).then(
      () => toast.success(`${label} copied to clipboard`),
      () => toast.error('Failed to copy')
    );
  };

  const handleToggle = async (b: BacklinkHubItem) => {
    try {
      await togglePosted({ formId: b.formId, index: b.backlinkIndex, posted: !b.posted }).unwrap();
      toast.success(b.posted ? 'Marked as pending' : 'Marked as posted');
    } catch (e: any) {
      toast.error(e?.data?.message || 'Failed to update');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-10 h-10 border-4 border-trident-orange border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return <div className="text-center py-10 text-red-500 font-semibold">Failed to load backlinks.</div>;
  }

  const stats = data || { total: 0, posted: 0, pending: 0, backlinks: [] };

  return (
    <div>
      <div className="mb-6">
        <h2 className="font-display font-bold text-2xl text-trident-gray-900">Backlinks Hub</h2>
        <p className="text-sm text-trident-gray-500">
          Centralized view of all AI-generated backlinks across your published blogs.
        </p>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-3 gap-3 mb-5">
        <div className="bg-white rounded-2xl border border-trident-gray-100 shadow-card p-4">
          <div className="text-xs font-display font-semibold text-trident-gray-500 uppercase tracking-wider mb-1">Total</div>
          <div className="font-display font-extrabold text-2xl text-trident-gray-900">{stats.total}</div>
        </div>
        <div className="bg-white rounded-2xl border border-green-100 shadow-card p-4">
          <div className="text-xs font-display font-semibold text-green-600 uppercase tracking-wider mb-1">Posted</div>
          <div className="font-display font-extrabold text-2xl text-green-600">{stats.posted}</div>
        </div>
        <div className="bg-white rounded-2xl border border-trident-orange/20 shadow-card p-4">
          <div className="text-xs font-display font-semibold text-trident-orange uppercase tracking-wider mb-1">Pending</div>
          <div className="font-display font-extrabold text-2xl text-trident-orange">{stats.pending}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 text-trident-gray-400" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          <input
            type="text"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
            placeholder="Search by blog title, thread title, or URL..."
            className="input-field pl-10"
          />
        </div>
        <div className="flex gap-2">
          {(['all', 'pending', 'posted'] as FilterStatus[]).map(f => (
            <button
              key={f}
              onClick={() => { setFilter(f); setCurrentPage(1); }}
              className={`px-4 py-2.5 rounded-xl font-display font-semibold text-sm transition-all capitalize ${
                filter === f
                  ? 'bg-trident-gradient text-white shadow-trident'
                  : 'bg-white border border-trident-gray-200 text-trident-gray-600 hover:border-trident-orange hover:text-trident-orange'
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      {/* List */}
      {filtered.length === 0 ? (
        <div className="card text-center py-16">
          <h3 className="font-display font-bold text-lg text-trident-gray-900 mb-1">No backlinks found</h3>
          <p className="text-sm text-trident-gray-500">Try adjusting your filters.</p>
        </div>
      ) : (
        <>
          <div className="space-y-3">
            {paginated.map((b, i) => (
              <div
                key={`${b.formId}-${b.backlinkIndex}-${i}`}
                className={`bg-white rounded-2xl border shadow-card p-4 transition-all ${
                  b.posted
                    ? 'border-green-200 bg-green-50/30'
                    : 'border-trident-gray-100 hover:border-trident-orange/40'
                }`}
              >
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={b.posted}
                    onChange={() => handleToggle(b)}
                    className="mt-1 w-5 h-5 rounded border-2 border-trident-gray-300 text-trident-orange focus:ring-trident-orange flex-shrink-0 cursor-pointer"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      <span className="text-[10px] font-display font-bold uppercase tracking-wider text-trident-gray-500">
                        For blog:
                      </span>
                      <a
                        href={b.publishedUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs font-display font-semibold text-trident-orange hover:underline truncate"
                      >
                        {b.blogTitle}
                      </a>
                      {b.posted && <span className="badge-green">✓ Posted</span>}
                    </div>
                    <h4 className="font-display font-bold text-sm text-trident-gray-900 mb-1 leading-snug">
                      {b.title}
                    </h4>
                    <a
                      href={b.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-blue-600 hover:underline break-all block mb-3"
                    >
                      {b.url}
                    </a>
                    <div className="bg-trident-gray-50 rounded-lg p-3 border border-trident-gray-100">
                      <div className="text-[10px] font-display font-bold uppercase tracking-wider text-trident-gray-500 mb-1">
                        Suggested comment
                      </div>
                      <p className="text-sm text-trident-gray-700 leading-relaxed italic">"{b.comment}"</p>
                    </div>
                    <div className="flex items-center gap-2 mt-3">
                      <button
                        onClick={() => copyText(b.url, 'URL')}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-trident-gray-200 text-xs font-display font-semibold text-trident-gray-600 hover:border-trident-orange hover:text-trident-orange transition"
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                        Copy URL
                      </button>
                      <button
                        onClick={() => copyText(b.comment, 'Comment')}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-trident-gray-200 text-xs font-display font-semibold text-trident-gray-600 hover:border-trident-orange hover:text-trident-orange transition"
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                        Copy Comment
                      </button>
                      {b.postedAt && (
                        <span className="text-[10px] text-trident-gray-400 ml-auto">
                          Posted {new Date(b.postedAt).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={filtered.length}
            itemsPerPage={ITEMS_PER_PAGE}
            onPageChange={setCurrentPage}
          />
        </>
      )}
    </div>
  );
};

export default BacklinksHub;
